/*
 * Helpers and constants
 */

export const domain = process.env.REACT_APP_FRONTEND;

const api = process.env.REACT_APP_API;
const apiVersion = process.env.REACT_APP_API_VERSION;

function cleanUrl(base, ...parts) {
  return `${base}/${parts
    .flat()
    .filter(Boolean) // Remove empty parts
    .join("/")
    .replaceAll("\\", "/")
    .replace(/\/+/g, "/") // Replace multiple slashes with a single slash
    .replace(/\/$/, "")}`; // Remove trailing slash
}

// The organizationPrefix segment is always the first path segment across this whole app's
// routing (public/private/admin routes all agree on this -- see src/routes/*.js), so any
// caller can recover it from the current URL rather than needing it passed in explicitly.
export function currentOrganizationPrefix() {
  const url = new URL(window.location.href);
  const pathSegments = url.pathname.split("/");
  return pathSegments[1];
}

export function publicUrl(...params) {
  return cleanUrl(api, apiVersion, `public`, ...params);
}

export function personalUrl(...params) {
  return cleanUrl(api, apiVersion, ...params);
}

export function privateUrl(...params) {
  const organizationPrefix = currentOrganizationPrefix();
  return cleanUrl(api, apiVersion, organizationPrefix, ...params);
}

export function adminUrl(...params) {
  const organizationPrefix = currentOrganizationPrefix();
  return cleanUrl(api, "admin", organizationPrefix, ...params);
}

export function adminNoOrgUrl(...params) {
  return cleanUrl(api, "admin", ...params);
}

export function specialUrl(...params) {
  const organizationPrefix = currentOrganizationPrefix();
  return cleanUrl(api, apiVersion, "special", organizationPrefix, ...params);
}

export function accessHeaders(accessToken, isJSON) {
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };
  if (!isJSON) delete headers[`Content-Type`];
  return {headers};
}

export function jsonBody(body) {
  return {body: JSON.stringify(body)};
}

// Constants for reload attempts
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_ATTEMPTS_KEY = "reloadAttempts";

// Helper functions for reload attempts
function getReloadAttempts() {
  return parseInt(localStorage.getItem(RELOAD_ATTEMPTS_KEY), 10) || 0;
}

function incrementReloadAttempts() {
  const attempts = getReloadAttempts() + 1;
  localStorage.setItem(RELOAD_ATTEMPTS_KEY, attempts);
}

function resetReloadAttempts() {
  localStorage.removeItem(RELOAD_ATTEMPTS_KEY);
}

// Function to handle requests
export function request({url, method = "POST", accessToken, body, raw = false}) {
  const isFormData = body instanceof FormData;
  let options = {
    method,
    ...accessHeaders(accessToken, !isFormData),
  };
  if (body) options.body = isFormData ? body : JSON.stringify({...body});
  if (raw) return fetch(url, options);

  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          if (
            (response.status === 401 && error.name === "InvalidTokenError") ||
            (response.status === 500 && error.message === '"exp" claim timestamp check failed')
          ) {
            const attempts = getReloadAttempts();
            if (attempts < MAX_RELOAD_ATTEMPTS) {
              incrementReloadAttempts();
              window.location.reload();
            } else {
              resetReloadAttempts();
              window.location.href = `/login`;
            }
            return null;
          }
          // Attach the real HTTP status so callers can distinguish e.g. 403
          // (forbidden -- a real permission boundary some callers want to
          // handle specially) from other failures, without string-matching
          // on `message`.
          error.status = response.status;
          throw error;
        });
      }
      resetReloadAttempts(); // Reset the counter on successful request
      return response.json();
    })
    .catch((error) => {
      // Handle other errors
      console.error("Request failed:", error);
      throw error;
    });
}
